import { useEffect, useState } from 'react'
import { Bell, BellOff, Check, X } from 'lucide-react'
import { useAuth } from '@cravo/auth'
import { useTranslation } from '@cravo/i18n'
import {
  dismissPermissionPrompt,
  getNotificationPermission,
  isNotificationApiAvailable,
  isPermissionPromptDismissed,
  isSoundEnabled,
  playNewOrderSound,
  requestNotificationPermission,
  setSoundEnabled,
} from '@/lib/order-alerter'
import { registerPushNotifications } from '@/lib/push-notifications'
import toast from 'react-hot-toast'

/**
 * Floating banner that appears on dashboard pages until the vendor either
 * grants browser notification permission or dismisses the prompt. Also
 * houses a small toggle for the in-app order sound.
 */
export function OrderAlertSettings() {
  const { t } = useTranslation()
  const { vendor } = useAuth()
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    getNotificationPermission(),
  )
  const [dismissed, setDismissed] = useState(() => isPermissionPromptDismissed())
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled())
  const [requesting, setRequesting] = useState(false)

  useEffect(() => {
    setPermission(getNotificationPermission())
  }, [])

  const handleEnable = async () => {
    if (!isNotificationApiAvailable()) {
      toast.error(t('components.orderAlert.notSupported'))
      return
    }
    setRequesting(true)
    try {
      const result = await requestNotificationPermission()
      setPermission(result)
      if (result === 'granted') {
        toast.success(t('components.orderAlert.enabledSuccess'))
        playNewOrderSound()
        // Register an FCM token so backend pushes reach this device even
        // when the tab is closed. Failure is non-fatal — fall back to
        // foreground polling + local browser notifications.
        if (vendor?.id) {
          const result = await registerPushNotifications(vendor.id)
          if (!result.ok && result.reason === 'no-vapid-key') {
            // Quiet — environment misconfiguration shouldn't surface to vendor
          }
        }
      } else if (result === 'denied') {
        toast.error(t('components.orderAlert.blocked'))
      }
    } finally {
      setRequesting(false)
    }
  }

  const handleDismiss = () => {
    dismissPermissionPrompt()
    setDismissed(true)
  }

  const toggleSound = () => {
    const next = !soundOn
    setSoundEnabled(next)
    setSoundOn(next)
    if (next) {
      playNewOrderSound()
    }
  }

  const showPrompt = !dismissed && permission === 'default' && isNotificationApiAvailable()

  if (!showPrompt && permission !== 'granted' && !isNotificationApiAvailable()) {
    return null
  }

  return (
    <>
      {showPrompt && (
        <div
          className="card"
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            maxWidth: 360,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            background: 'white',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 200,
            border: '1px solid var(--brand-pink-100)',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'rgba(255,45,151,0.10)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Bell size={20} color="var(--brand-pink)" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-950)' }}>
              {t('components.orderAlert.heading')}
            </div>
            <p style={{ margin: '4px 0 10px', fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5 }}>
              {t('components.orderAlert.body')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn primary sm" onClick={() => void handleEnable()} disabled={requesting}>
                {requesting ? t('components.orderAlert.requesting') : t('components.orderAlert.enable')}
              </button>
              <button className="btn sm" onClick={handleDismiss} disabled={requesting}>
                {t('components.orderAlert.notNow')}
              </button>
            </div>
          </div>
          <button
            className="btn sm"
            onClick={handleDismiss}
            style={{ padding: '4px 6px' }}
            aria-label={t('components.orderAlert.dismiss')}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={toggleSound}
        title={soundOn ? t('components.orderAlert.soundOn') : t('components.orderAlert.soundOff')}
        aria-pressed={soundOn}
        style={{
          position: 'fixed',
          right: 20,
          bottom: showPrompt ? 160 : 20,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'white',
          border: '1px solid var(--ink-150)',
          boxShadow: 'var(--shadow-md)',
          display: 'grid',
          placeItems: 'center',
          color: soundOn ? 'var(--brand-pink)' : 'var(--ink-500)',
          zIndex: 199,
          cursor: 'pointer',
          transition: 'all 150ms',
        }}
      >
        {soundOn ? <Bell size={18} /> : <BellOff size={18} />}
        {permission === 'granted' && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: 'var(--success)',
              border: '2px solid white',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Check size={6} color="white" />
          </span>
        )}
      </button>
    </>
  )
}
